import * as THREE from 'three';
import { SPEC, ft } from '../core/units.js';
import { createBroadphase } from '../core/collision.js';

/**
 * Turns the plain level data in `src/data/level.js` into geometry and into the
 * boxes the collision reads — from the same source, in one pass.
 *
 * That single-source rule is the whole reason this module exists. A wall you
 * can see but not hit, or hit but not see, is the bug a blockout generates
 * fastest and hides longest, and the only reliable fix is to make it
 * unrepresentable: `levelSolids()` produces one list, the geometry is drawn
 * from it and the broadphase is built from it.
 *
 * Level data is written in feet (see `units.js`) and is *declarative about
 * openings*: a wall is a segment with a list of doors cut into it, not a list
 * of pre-split pieces. Authoring the pieces by hand is how door jambs end up a
 * few inches out of line with the room they open into, and it makes moving a
 * door a five-edit operation.
 */

/** A door leaves a header above it, so the wall still reads as a wall. */
export function splitWall(wall) {
  const [x0, z0] = wall.from;
  const [x1, z1] = wall.to;
  const horizontal = Math.abs(x1 - x0) >= Math.abs(z1 - z0);
  const start = horizontal ? x0 : z0;
  const end = horizontal ? x1 : z1;
  const sign = Math.sign(end - start) || 1;
  const length = Math.abs(end - start);
  const height = wall.height ?? 9;

  const doors = [...(wall.doors ?? [])].sort((a, b) => a.at - b.at);
  const pieces = [];
  let cursor = 0;

  for (const door of doors) {
    const from = Math.max(0, Math.min(door.at, length));
    const to = Math.max(from, Math.min(door.at + door.width, length));
    const doorHeight = Math.min(door.height ?? 7, height);
    if (from > cursor) pieces.push({ from: cursor, to: from, base: 0, top: height });
    // The header: the strip of wall above the opening. Skipped when the door
    // runs the full height, which is what a dock roll-up door does.
    if (doorHeight < height) pieces.push({ from, to, base: doorHeight, top: height });
    cursor = to;
  }
  if (cursor < length) pieces.push({ from: cursor, to: length, base: 0, top: height });

  const half = (wall.thickness ?? 0.5) / 2;
  const cross = horizontal ? z0 : x0;

  return pieces.map((piece, index) => {
    const a = start + sign * piece.from;
    const b = start + sign * piece.to;
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return {
      id: `${wall.id}:${index}`,
      tag: wall.tag ?? 'wall',
      color: wall.color,
      min: horizontal
        ? { x: lo, y: piece.base, z: cross - half }
        : { x: cross - half, y: piece.base, z: lo },
      max: horizontal
        ? { x: hi, y: piece.top, z: cross + half }
        : { x: cross + half, y: piece.top, z: hi },
    };
  });
}

/** Feet-space box to metres-space box. */
function toMetres(id, tag, min, max) {
  return {
    id,
    tag,
    min: { x: ft(min.x), y: ft(min.y), z: ft(min.z) },
    max: { x: ft(max.x), y: ft(max.y), z: ft(max.z) },
  };
}

function rectToMetres([x0, z0, x1, z1]) {
  return {
    minX: ft(Math.min(x0, x1)),
    maxX: ft(Math.max(x0, x1)),
    minZ: ft(Math.min(z0, z1)),
    maxZ: ft(Math.max(z0, z1)),
  };
}

/**
 * Every box in the level, in metres, with no renderer involved.
 *
 * Split out from `buildLevel` so the blockout can be *tested*. A connectivity
 * check that has to stand up a WebGL context is a check nobody runs, and
 * "can you actually walk from the break room to the ballroom" is the one
 * question a blockout has to keep answering yes to while it is edited.
 */
export function levelSolids(level) {
  const out = [];

  for (const room of level.rooms ?? []) {
    const [x0, z0, x1, z1] = room.rect;
    const base = room.base ?? 0;
    out.push({
      tag: 'floor',
      color: room.floorColor ?? 0x54585f,
      metres: toMetres(
        `floor:${room.id}`,
        'floor',
        { x: x0, y: base - 0.5, z: z0 },
        { x: x1, y: base, z: z1 }
      ),
    });
    if (room.ceiling) {
      out.push({
        tag: 'ceiling',
        color: room.ceilingColor ?? 0x5f6470,
        metres: toMetres(
          `ceiling:${room.id}`,
          'ceiling',
          { x: x0, y: base + room.ceiling, z: z0 },
          { x: x1, y: base + room.ceiling + 0.5, z: z1 }
        ),
      });
    }
  }

  for (const wall of level.walls ?? []) {
    for (const piece of splitWall({ thickness: 0.5, ...wall })) {
      out.push({
        tag: piece.tag,
        color: piece.color ?? wall.color ?? 0x6b6f77,
        metres: toMetres(piece.id, piece.tag, piece.min, piece.max),
      });
    }
  }

  for (const barrier of level.barriers ?? []) {
    const [x0, z0, x1, z1] = barrier.rect;
    const base = barrier.base ?? 0;
    out.push({
      tag: 'barrier',
      barrier: barrier.id,
      color: barrier.color ?? 0x8c2f3a,
      // Off by default. `buildLevel` hands back a switch for each one.
      solid: false,
      visible: false,
      opacity: barrier.opacity ?? 1,
      metres: toMetres(
        `barrier:${barrier.id}`,
        'barrier',
        { x: Math.min(x0, x1), y: base, z: Math.min(z0, z1) },
        { x: Math.max(x0, x1), y: base + barrier.height, z: Math.max(z0, z1) }
      ),
    });
  }

  // Growth at rider height. Drawn, never collided with: a cart passes under a
  // branch and a person pushes through a bush.
  for (const swipe of level.swipes ?? []) {
    const [x0, z0, x1, z1] = swipe.rect;
    out.push({
      tag: 'swipe',
      color: swipe.kind === 'bush' ? 0x5d6b3f : 0x7d8f4e,
      solid: false,
      visible: true,
      opacity: 0.85,
      metres: toMetres(
        `swipe:${swipe.id}`,
        'swipe',
        { x: Math.min(x0, x1), y: swipe.base, z: Math.min(z0, z1) },
        { x: Math.max(x0, x1), y: swipe.base + swipe.height, z: Math.max(z0, z1) }
      ),
    });
  }

  for (const prop of level.props ?? []) {
    const [x0, z0, x1, z1] = prop.rect;
    const base = prop.base ?? 0;
    out.push({
      tag: prop.tag ?? 'prop',
      color: prop.color ?? 0x7a6a58,
      solid: prop.solid !== false,
      visible: prop.visible !== false,
      opacity: prop.opacity ?? 1,
      metres: toMetres(
        `prop:${prop.id}`,
        prop.tag ?? 'prop',
        { x: Math.min(x0, x1), y: base, z: Math.min(z0, z1) },
        { x: Math.max(x0, x1), y: base + prop.height, z: Math.max(z0, z1) }
      ),
    });
  }

  return out;
}

/**
 * Just the collidable boxes, in metres. What the broadphase is built from.
 *
 * `closed` names barriers that are currently in place, so a test can ask what
 * the property is like with a door chained without standing up a renderer.
 */
export function collisionBoxes(level, { closed = [] } = {}) {
  const shut = new Set(closed);
  return levelSolids(level)
    .filter((entry) => (entry.barrier ? shut.has(entry.barrier) : entry.solid !== false))
    .map((entry) => entry.metres);
}

export function buildLevel(level) {
  const group = new THREE.Group();
  group.name = `level-${level.name ?? 'unnamed'}`;
  const materials = new Map();
  const material = (color, opts = {}) => {
    const key = `${color}|${JSON.stringify(opts)}`;
    let found = materials.get(key);
    if (!found) {
      found = new THREE.MeshStandardMaterial({ color, roughness: 0.95, metalness: 0, ...opts });
      materials.set(key, found);
    }
    return found;
  };

  const solids = [];
  const geometries = [];
  const barrierMeshes = new Map();
  const closed = new Set();

  for (const entry of levelSolids(level)) {
    if (entry.solid !== false) solids.push(entry.metres);
    if (entry.visible === false && !entry.barrier) continue;

    const { min, max } = entry.metres;
    const size = {
      x: Math.max(max.x - min.x, 0.001),
      y: Math.max(max.y - min.y, 0.001),
      z: Math.max(max.z - min.z, 0.001),
    };
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    geometries.push(geometry);
    const mesh = new THREE.Mesh(
      geometry,
      material(entry.color, entry.opacity < 1 ? { transparent: true, opacity: entry.opacity } : {})
    );
    mesh.name = entry.metres.id;
    mesh.position.set((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2);
    // Ceilings and floors are solid but invisible to the light on purpose. One
    // directional key is the whole lighting budget, and a ceiling that casts
    // puts every interior room in full shadow — which is what a real ceiling
    // does, and which in a greybox reads as "the level failed to load". Walls
    // and props still cast, so the corridor keeps depth.
    mesh.castShadow = entry.solid !== false && entry.tag !== 'ceiling' && entry.tag !== 'floor';
    mesh.receiveShadow = true;
    if (entry.barrier) {
      mesh.visible = false;
      barrierMeshes.set(entry.barrier, mesh);
    }
    group.add(mesh);
  }

  const rooms = (level.rooms ?? []).map((room) => ({
    ...room,
    bounds: rectToMetres(room.rect),
    ceilingMetres: ft(room.ceiling ?? 0),
  }));
  const zones = (level.zones ?? []).map((zone) => ({ ...zone, bounds: rectToMetres(zone.rect) }));
  const swipes = swipesOf(level);

  const world = createBroadphase(solids, { cellSize: ft(12) });

  /**
   * Opens or closes a barrier, and rebuilds the broadphase.
   *
   * A full rebuild rather than an incremental insert. The grid is a hundred
   * boxes and takes under a millisecond to build; barriers change when an event
   * is accepted or a shortfall is resolved, which is a handful of times a
   * shift. Keeping the structure immutable between rebuilds is worth far more
   * than the microsecond an incremental path would save.
   */
  function setBarrier(id, on) {
    if (!barrierMeshes.has(id)) return false;
    if (on === closed.has(id)) return true;
    if (on) closed.add(id);
    else closed.delete(id);
    barrierMeshes.get(id).visible = on;
    world.rebuild(collisionBoxes(level, { closed: [...closed] }));
    return true;
  }

  return {
    object3D: group,
    world,
    setBarrier,
    get closedBarriers() {
      return [...closed];
    },
    solids,
    rooms,
    zones,
    swipes,
    level,
    spawn: {
      x: ft(level.spawn?.x ?? 0),
      y: ft(level.spawn?.y ?? 0),
      z: ft(level.spawn?.z ?? 0),
      yaw: level.spawn?.yaw ?? 0,
    },
    /** Every zone covering a point. Cheap: there are a handful, not a thousand. */
    zonesAt(x, z) {
      return zones.filter((zone) => inBounds(x, z, zone.bounds));
    },
    roomAt(x, z) {
      return rooms.find((room) => inBounds(x, z, room.bounds)) ?? null;
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const found of materials.values()) found.dispose();
      group.removeFromParent();
    },
  };
}

/** The swipe volumes, in metres, as boxes a wheeled thing can test a seat against. */
export function swipesOf(level) {
  return (level.swipes ?? []).map((swipe) => {
    const [x0, z0, x1, z1] = swipe.rect;
    return {
      id: swipe.id,
      kind: swipe.kind,
      min: { x: ft(Math.min(x0, x1)), y: ft(swipe.base), z: ft(Math.min(z0, z1)) },
      max: { x: ft(Math.max(x0, x1)), y: ft(swipe.base + swipe.height), z: ft(Math.max(z0, z1)) },
    };
  });
}

export function inBounds(x, z, bounds) {
  return x >= bounds.minX && x <= bounds.maxX && z >= bounds.minZ && z <= bounds.maxZ;
}

export { SPEC };
